import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { State } from '../../types/state'
import { useRequests } from '../../requests/useRequests'
import { RefreshActions } from '../../state/refresh'
import Header from '../Header/Header'
import Footer from '../Footer/Footer'
import PricingComparisonTable from '../Shared/PricingComparisonTable'

type Package = 'START' | 'PLUS' | 'PREMIUM'

// Pricing tables for PLUS and PREMIUM plans
const PLUS_MONTHLY_PRICING: { [key: number]: number } = {
  1: 70,
  2: 62.93,
  3: 58.93,
  4: 51.11,
  5: 47.11,
  6: 43.19,
  7: 39.33,
  8: 35.37,
  9: 32.22,
  10: 29.87,
  11: 29.07,
  12: 28.23,
  13: 27.48,
  14: 27.05,
  15: 26.69,
  16: 25.91,
  17: 25.92,
  18: 25.13,
  19: 24.37,
  20: 23.54
}

const PREMIUM_MONTHLY_PRICING: { [key: number]: number } = {
  1: 95,
  2: 85.39,
  3: 80.03,
  4: 69.41,
  5: 63.99,
  6: 58.59,
  7: 53.35,
  8: 48.02,
  9: 43.69,
  10: 40.51,
  11: 39.43,
  12: 38.31,
  13: 37.29,
  14: 36.77,
  15: 36.25,
  16: 35.17,
  17: 35.19,
  18: 34.07,
  19: 33.01,
  20: 32
}

const PLUS_YEARLY_PRICING: { [key: number]: number } = {
  1: 720,
  2: 1294.56,
  3: 1814,
  4: 2098.32,
  5: 2421.60,
  6: 2665.32,
  7: 2821.44,
  8: 2914.16,
  9: 2985.48,
  10: 3069.60,
  11: 3258.80,
  12: 3443.28,
  13: 3615.72,
  14: 3784.80,
  15: 3950.40,
  16: 4112.80,
  17: 4275.72,
  18: 4434.36,
  19: 4587.44,
  20: 4835.20
}

const PREMIUM_YEARLY_PRICING: { [key: number]: number } = {
  1: 1020,
  2: 1834.08,
  3: 2570.44,
  4: 2973.92,
  5: 3434.40,
  6: 3769.36,
  7: 4044.40,
  8: 4275.36,
  9: 4434.48,
  10: 4634.80,
  11: 4846.42,
  12: 5094.96,
  13: 5340.36,
  14: 5582.52,
  15: 5821.80,
  16: 6055.52,
  17: 6285.36,
  18: 6511.68,
  19: 6734.52,
  20: 6953.20
}

const CheckIcon = () => (
  <svg className='tw-w-5 tw-h-5 tw-text-green tw-flex-shrink-0' xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='currentColor'>
    <path fillRule='evenodd' d='M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.052-.143z' clipRule='evenodd' />
  </svg>
)

const PlusIconSm = () => (
  <svg className='tw-w-5 tw-h-5' xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='currentColor'>
    <path d='M10.75 4.75a.75.75 0 00-1.5 0v4.5h-4.5a.75.75 0 000 1.5h4.5v4.5a.75.75 0 001.5 0v-4.5h4.5a.75.75 0 000-1.5h-4.5v-4.5z' />
  </svg>
)

const MinusIconSm = () => (
  <svg className='tw-w-5 tw-h-5' xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20' fill='currentColor'>
    <path fillRule='evenodd' d='M4 10a.75.75 0 01.75-.75h10.5a.75.75 0 010 1.5H4.75A.75.75 0 014 10z' clipRule='evenodd' />
  </svg>
)

const ArrowRightIcon = () => (
  <svg className='tw-w-4 tw-h-4 tw-ml-1.5' xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' strokeWidth={2} stroke='currentColor'>
    <path strokeLinecap='round' strokeLinejoin='round' d='M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3' />
  </svg>
)

interface PricingPlanCardProps {
  planName: string
  planKey: 'START' | 'PLUS' | 'PREMIUM'
  descriptionTop: string
  basePriceMonthly: number
  features: string[]
  period: 'month' | 'year'
  usersCount: number
  onUserCountChange?: (plan: string, change: number) => void
  calculatePrice: (basePrice: number, plan: string) => number
  calculateYearlyOriginalPrice: (basePrice: number, plan: string) => number
  onBuyNow: (plan: string, basePrice: number) => void
  onActivateTrial?: () => Promise<void>
  canUseTrial?: boolean
  isTrialActivating?: boolean
  isLoggedIn: boolean
  userPackage?: Package | null
  hadPremiumTrial?: boolean
  isPopular?: boolean
  showComparisonTable?: boolean
}

const PricingPlanCard: React.FC<PricingPlanCardProps> = ({
  planName,
  planKey,
  descriptionTop,
  basePriceMonthly,
  features,
  period,
  usersCount,
  onUserCountChange,
  calculatePrice,
  calculateYearlyOriginalPrice,
  onBuyNow,
  onActivateTrial,
  canUseTrial,
  isTrialActivating,
  isLoggedIn,
  userPackage,
  hadPremiumTrial,
  isPopular,
  showComparisonTable
}) => {
  const currentPrice = calculatePrice(basePriceMonthly, planKey)
  const yearlyOriginalPrice = calculateYearlyOriginalPrice(basePriceMonthly, planKey)

  const userPriceInfo = () => {
    if (planKey === 'PLUS' || planKey === 'PREMIUM') {
      if (usersCount > 1) {
        if (period === 'month') {
          return 'za użytkownika'
        } else {
          const pricePerUser = currentPrice / usersCount
          return `${pricePerUser.toFixed(2)} zł/użytkownika`
        }
      }
    }
    return null
  }

  return (
    <div className={`
      tw-bg-white tw-rounded-xl tw-shadow-lg tw-p-6 md:tw-p-8 tw-flex tw-flex-col tw-h-full tw-relative
      ${isPopular ? 'tw-border-2 tw-border-green' : 'tw-border tw-border-slate-200'} 
    `}
    >
      {isPopular && (
        <div className='tw-absolute tw-top-0 tw-right-6 tw--translate-y-1/2 tw-bg-green tw-text-white tw-text-xs tw-font-semibold tw-px-3 tw-py-1 tw-rounded-full'>
          Popularny
        </div>
      )}
      <div className='tw-flex-grow'>
        <p className='tw-text-sm tw-text-slate-500 tw-mb-2 tw-min-h-[3em]'>{descriptionTop}</p>
        <h3 className='tw-text-2xl tw-font-bold tw-text-teal tw-mb-1'>{planName}</h3>

        <div className='tw-mb-6 tw-min-h-[5em]'>
          {period === 'month'
            ? (
              <div className='tw-flex tw-items-end tw-space-x-1'>
                <span className='tw-text-4xl tw-font-extrabold tw-text-teal'>{currentPrice.toFixed(2)}</span>
                <span className='tw-text-slate-500 tw-pb-1'>zł/30 dni</span>
              </div>
              )
            : (
              <div>
                <div className='tw-flex tw-items-end tw-space-x-1'>
                  <span className='tw-text-4xl tw-font-extrabold tw-text-teal'>{currentPrice.toFixed(2)}</span>
                  <span className='tw-text-slate-500 tw-pb-1'>zł/rok</span>
                </div>
                <div className='tw-text-sm tw-text-slate-400'>
                  <s>{yearlyOriginalPrice.toFixed(2)} zł</s>
                  <span className='tw-ml-2 tw-text-pink-600 tw-font-semibold'>-15%</span>
                </div>
              </div>
              )}
          {(planKey === 'PLUS' || planKey === 'PREMIUM') && usersCount > 1 && (
            <div className='tw-mt-2 tw-text-sm tw-text-slate-500'>
              {period === 'month' ? 'za użytkownika' : `${(currentPrice / usersCount).toFixed(2)} zł/użytkownika`}
            </div>
          )}
        </div>

        {(planKey === 'PLUS' || planKey === 'PREMIUM') && (onUserCountChange != null) && (
          <div className='tw-mb-6'>
            <p className='tw-text-sm tw-font-medium tw-text-slate-700 tw-mb-2'>Liczba użytkowników:</p>
            <div className='tw-flex tw-items-center tw-justify-center tw-space-x-3'>
              <button
                onClick={() => onUserCountChange(planKey, -1)}
                disabled={usersCount <= 1}
                className='tw-w-8 tw-h-8 tw-flex tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-slate-300 hover:tw-bg-slate-100 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed'
              >
                <MinusIconSm />
              </button>
              <span className='tw-text-lg tw-font-semibold tw-text-teal'>{usersCount}</span>
              <button
                onClick={() => onUserCountChange(planKey, 1)}
                disabled={usersCount >= 20}
                className='tw-w-8 tw-h-8 tw-flex tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-slate-300 hover:tw-bg-slate-100 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed'
              >
                <PlusIconSm />
              </button>
            </div>
          </div>
        )}

        <ul className='tw-space-y-3 tw-mb-8'>
          {features.map((feature, index) => (
            <li key={index} className='tw-flex tw-items-start'>
              <CheckIcon />
              <span className='tw-ml-2 tw-text-sm tw-text-slate-600'>{feature}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className='tw-mt-auto'>
        <button
          onClick={() => onBuyNow(planKey, basePriceMonthly)}
          className='tw-w-full tw-flex tw-items-center tw-justify-center tw-text-center tw-font-semibold tw-rounded-md tw-shadow-sm tw-transition-colors tw-bg-green hover:tw-bg-opacity-90 tw-text-white tw-px-4 tw-py-2 tw-text-sm lg:tw-px-5 lg:tw-py-2.5 lg:tw-text-base'
        >
          Wykup plan <ArrowRightIcon />
        </button>
        {planKey === 'PREMIUM' && canUseTrial && (onActivateTrial != null) && (
          <button
            onClick={onActivateTrial}
            disabled={isTrialActivating}
            className='tw-mt-3 tw-w-full tw-flex tw-items-center tw-justify-center tw-text-center tw-font-semibold tw-rounded-md tw-shadow-md tw-transition-colors tw-bg-white hover:tw-bg-opacity-90 tw-text-teal tw-px-4 tw-py-2 tw-text-sm lg:tw-px-5 lg:tw-py-2.5 lg:tw-text-base'
          >
            {isTrialActivating ? 'Aktywowanie...' : 'Wypróbuj za darmo przez 30 dni'}
          </button>
        )}
        {planKey === 'PREMIUM' && isLoggedIn && hadPremiumTrial && (
          <p className='tw-mt-3 tw-text-center tw-text-xs tw-text-slate-500'>
            Wykorzystałeś już swoją wersję próbną.
          </p>
        )}
        {planKey === 'PREMIUM' && isLoggedIn && userPackage === 'PREMIUM' && !hadPremiumTrial && (
          <p className='tw-mt-3 tw-text-center tw-text-xs tw-text-green tw-font-semibold'>
            Aktualnie używasz tego pakietu.
          </p>
        )}
      </div>
    </div>
  )
}

const Pricing = ({ showHeader, showComparisonTable }: { showHeader?: boolean, showComparisonTable?: boolean }) => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const requestMaker = useRequests()
  const [period, setPeriod] = useState<'month' | 'year'>('month')
  const [usersCount, setUsersCount] = useState<{ [key: string]: number }>({
    START: 1,
    PLUS: 1,
    PREMIUM: 1
  })
  const [isTrialActivating, setIsTrialActivating] = useState(false)

  const userState = useSelector((state: State) => state.user)
  const { username, hadPremiumTrial, package: userPackage } = userState

  const handleUserCountChange = (plan: string, change: number) => {
    if (plan === 'START') return
    const currentPlanCount = usersCount[plan]
    let newCount = currentPlanCount + change

    if (plan === 'PLUS') {
      newCount = Math.min(Math.max(newCount, 1), 20)
    } else if (plan === 'PREMIUM') {
      newCount = Math.min(Math.max(newCount, 1), 20)
    }
    setUsersCount(prevCounts => ({ ...prevCounts, [plan]: newCount }))
  }

  const calculatePrice = (basePrice: number, plan: string) => {
    if (plan === 'START') {
      return period === 'year' ? basePrice * 12 * 0.85 : basePrice
    }

    const userCount = usersCount[plan]
    let price: number

    if (plan === 'PLUS') {
      if (period === 'month') {
        price = PLUS_MONTHLY_PRICING[userCount] || PLUS_MONTHLY_PRICING[20]
      } else {
        price = PLUS_YEARLY_PRICING[userCount] || PLUS_YEARLY_PRICING[20]
      }
    } else if (plan === 'PREMIUM') {
      if (period === 'month') {
        price = PREMIUM_MONTHLY_PRICING[userCount] || PREMIUM_MONTHLY_PRICING[20]
      } else {
        price = PREMIUM_YEARLY_PRICING[userCount] || PREMIUM_YEARLY_PRICING[20]
      }
    } else {
      return basePrice
    }

    return price
  }

  const calculateYearlyOriginalPrice = (basePrice: number, plan: string) => {
    if (plan === 'START') {
      return basePrice * 12
    }

    const userCount = usersCount[plan]
    let price: number

    if (plan === 'PLUS') {
      price = PLUS_YEARLY_PRICING[userCount] || PLUS_YEARLY_PRICING[20]
    } else if (plan === 'PREMIUM') {
      price = PREMIUM_YEARLY_PRICING[userCount] || PREMIUM_YEARLY_PRICING[20]
    } else {
      return basePrice * 12
    }

    // Calculate original price without discount
    return price / 0.85
  }

  const handleBuyNow = (plan: string, basePrice: number) => {
    if (!username) {
      navigate('/login')
      return
    }
    const finalPrice = calculatePrice(basePrice, plan)
    navigate('/order-summary', {
      state: {
        plan,
        price: finalPrice.toFixed(2).replace('.', ','),
        currency: 'zł',
        frequency: period === 'month' ? '/miesiąc' : '/rok',
        usersCount: usersCount[plan]
      }
    })
  }

  const handleActivateTrial = async () => {
    if (!username) {
      navigate('/login')
      return
    }
    setIsTrialActivating(true)
    try {
      await requestMaker.activatePremiumTrial()
      dispatch(RefreshActions.setToastMessage({ message: 'Wersja próbna pakietu PREMIUM została aktywowana na 14 dni' }))
      dispatch(RefreshActions.refreshUserState())
    } catch (error: any) {
      dispatch(RefreshActions.setToastMessage({ message: error?.data?.message || 'Wystąpił błąd podczas aktywacji wersji próbnej' }))
    } finally {
      setIsTrialActivating(false)
    }
  }

  const [canUseTrial, setCanUseTrial] = useState<boolean>(false)
  useEffect(() => {
    setCanUseTrial(!username || (!!username && !hadPremiumTrial && userPackage !== 'PREMIUM'))
  }, [username, hadPremiumTrial, userPackage])

  const plans = [
    {
      name: 'START',
      key: 'START' as 'START' | 'PLUS' | 'PREMIUM',
      descriptionTop: 'Dla osób początkujących z niewielką ilością projektów',
      basePriceMonthly: 35,
      features: [
        'Możliwość stworzenia 30 projektów',
        'Dodawanie produktów do listy bezpośrednio ze stron internetowych',
        'Eksport list do pliku PDF',
        'Możliwość utworzenia firmowej wizytówki z danymi kontaktowymi wraz z portfolio'
      ],
      usersCountInfo: 'Liczna użytkowników 1'
    },
    {
      name: 'PLUS',
      key: 'PLUS' as 'START' | 'PLUS' | 'PREMIUM',
      descriptionTop: 'Dla projektantów, którzy koncentrują się na tworzeniu zestawień produktów, wycen dla klientów, ale nie wymagają narzędzi do zarządzania projektem ani zaawansowanej funkcji komunikacyjnych z zespołem',
      basePriceMonthly: 70,
      features: [
        'Nielimitowana ilość projektów',
        'Dodawanie produktów do listy bezpośrednio ze stron internetowych',
        'Eksport list do pliku Excel z możliwością ukrywania danych, np. ceny',
        'Eksport list do pliku PDF',
        'Eksport do moodboard',
        'Możliwość utworzenia firmowej wizytówki z danymi kontaktowymi, logo firmowe wraz z portfolio',
        'Udostępnienie ofert/listy produktów dla klientów, którzy mają możliwość akceptacji oraz pisania komentarzy',
        'Możliwość kopiowania, przenoszenia produktów na liście',
        'Własne logo i dane pracowni'
      ],
      usersCountInfo: 'Liczba użytkowników 1-20'
    },
    {
      name: 'PREMIUM',
      key: 'PREMIUM' as 'START' | 'PLUS' | 'PREMIUM',
      descriptionTop: 'Dla projektantów i biur projektowych, którzy szukają kompleksowego narzędzia do zarządzania każdym etapem realizacji',
      basePriceMonthly: 95,
      features: [
        'Nielimitowana ilość projektów',
        'Dodawanie produktów do listy bezpośrednio ze stron internetowych',
        'Eksport ofert/list do pliku Excel z możliwością ukrywania danych, np. ceny',
        'Eksport list do pliku PDF',
        'Eksport do moodboard',
        'Możliwość utworzenia firmowej wizytówki z danymi kontaktowymi, logo firmowe wraz z portfolio',
        'Udostępnienie ofert/list produktów dla klientów, którzy mają możliwość akceptacji oraz pisania komentarzy',
        'Możliwość przenoszenia produktów na liście',
        'Możliwość kopiowania',
        'Własne logo i dane pracowni',
        'Dostęp członków zespołu do listy produktów z możliwością edytowania',
        'Dodawanie załączników',
        'Panel Zarządzanie projektem (wszystkie informacje o projekcie w jednym panelu)',
        'Planowanie pracy w harmonogramie wraz z Zespołem',
        'Moduł zadań'
      ],
      usersCountInfo: 'Liczba użytkowników 1-20',
      isPopular: true
    }
  ]

  return (
    <>
      {showHeader && <Header />}
      <section id='pricing' className='tw-bg-slate-50 tw-pt-20 md:tw-pt-28 lg:tw-pt-36 tw-pb-12 md:tw-pb-20'>
        <div className='tw-container tw-mx-auto tw-px-4'>
          <div className='tw-max-w-2xl tw-mx-auto tw-text-center tw-mb-12 md:tw-mb-16'>
            <h1 className='tw-text-2xl md:tw-text-3xl lg:tw-text-4xl tw-font-bold tw-text-teal tw-mb-4'>Wybierz plan dopasowany do Ciebie</h1>
            <p className='tw-text-slate-600 tw-text-lg'>Proste ceny, bez ukrytych opłat. Zacznij już dziś!</p>
          </div>

          <div className='tw-flex tw-justify-center tw-items-center tw-mb-10 md:tw-mb-12'>
            <span className={`tw-px-3 tw-py-1 tw-text-sm tw-font-medium ${period === 'month' ? 'tw-text-teal' : 'tw-text-slate-500'}`}>
              Miesięcznie
            </span>
            <button
              type='button'
              onClick={() => setPeriod(period === 'month' ? 'year' : 'month')}
              className={`
                    tw-relative tw-inline-flex tw-flex-shrink-0 
                    tw-h-6 tw-w-11
                    tw-border /* Utrzymujemy 1px border dla spójności */
                    tw-rounded-full tw-cursor-pointer 
                    tw-transition-colors tw-duration-200 tw-ease-in-out 
                    focus:tw-outline-none /* Usuwa domyślny outline przeglądarki */
            
                    ${period === 'year'
                                  ? 'tw-bg-green tw-border-green focus:tw-bg-green focus:tw-border-green' /* Stan aktywny (zielony) - ten jest już OK */
                                  : 'tw-bg-slate-300 hover:tw-bg-slate-400 tw-border-slate-400 focus:tw-border-slate-500' /* Stan nieaktywny - bardziej widoczny */
                              }
                `}
              role='switch'
              aria-checked={period === 'year'}
            >
              <span className='tw-sr-only'>Wybierz okres rozliczeniowy</span>
              <span
                aria-hidden='true'
                className={`
                        tw-pointer-events-none tw-inline-block 
                        tw-h-5 tw-w-5 
                        tw-transform tw-rounded-full tw-bg-white tw-shadow-lg tw-ring-0 
                        tw-transition tw-duration-200 tw-ease-in-out 
                        ${period === 'year' ? 'tw-translate-x-5' : 'tw-translate-x-0'}
                    `}
              />
            </button>
            <span className={`tw-px-3 tw-py-1 tw-text-sm tw-font-medium ${period === 'year' ? 'tw-text-teal' : 'tw-text-slate-500'}`}>
              Rocznie
              <span className='tw-ml-2 tw-bg-pink-100 tw-text-pink-700 tw-text-xs tw-font-bold tw-px-2 tw-py-0.5 tw-rounded-full'>
                Oszczędź 15%
              </span>
            </span>
          </div>
          <p className='tw-text-center tw-text-xs tw-text-slate-500 tw-mb-12 md:tw-mb-16'>Wszystkie ceny są cenami brutto (zawierają podatek VAT)</p>

          <div className='tw-grid tw-grid-cols-1 lg:tw-grid-cols-3 tw-gap-8 xl:tw-gap-10'>
            {plans.map((plan) => (
              <div key={plan.key}>
                <PricingPlanCard
                  key={plan.key}
                  planName={plan.name}
                  planKey={plan.key}
                  descriptionTop={plan.descriptionTop}
                  basePriceMonthly={plan.basePriceMonthly}
                  features={plan.features}
                  period={period}
                  usersCount={usersCount[plan.key]}
                  onUserCountChange={plan.key !== 'START' ? handleUserCountChange : undefined}
                  calculatePrice={calculatePrice}
                  calculateYearlyOriginalPrice={calculateYearlyOriginalPrice}
                  onBuyNow={handleBuyNow}
                  onActivateTrial={plan.key === 'PREMIUM' ? handleActivateTrial : undefined}
                  canUseTrial={plan.key === 'PREMIUM' ? canUseTrial : undefined}
                  isTrialActivating={plan.key === 'PREMIUM' ? isTrialActivating : undefined}
                  isLoggedIn={!!username}
                  userPackage={userPackage}
                  hadPremiumTrial={hadPremiumTrial}
                  isPopular={plan.key === 'PREMIUM'}
                />
              </div>
            ))}
          </div>
        </div>
      </section>
      {showComparisonTable && <PricingComparisonTable />}
      {showHeader && <Footer />}
    </>
  )
}

export default Pricing
